import { DEFAULT_SCOPES, type Scope } from '@mesa/core/auth/types';
import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table/data-table';
import { DataTableSelectionToolbar } from '@/components/data-table/selection-toolbar';
import { DataTableToolbar } from '@/components/data-table/toolbar';
import { DataTableViewOptions } from '@/components/data-table/view-options';
import { ErrorState } from '@/components/error-state';
import { LabelEditor } from '@/components/labels/label-editor-popover';
import { LabelFilter } from '@/components/labels/label-filter';
import { RepoMultiSelect, type RepoOption } from '@/components/repo-multi-select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CodeBlock } from '@/components/ui/code-block';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGitBaseUrl } from '@/hooks/use-org';
import { pushError } from '@/lib/faro';
import { orgBySlugQueryOptions } from '@/lib/org-query-options';
import { ensureOrPrefetchInfiniteQuery } from '@/lib/query-utils';
import { highlightCode } from '@/lib/shiki';
import { ENV_SNIPPET, SDK_INIT_SNIPPET } from '@/lib/snippets';
import { tokensInfiniteQueryOptions, tokensQueryKey } from '@/lib/tokens-query-options';
import {
  bulkRevokeApiKeysServerFn,
  createApiKeyServerFn,
  revokeApiKeyServerFn,
  updateApiKeyTagsServerFn,
} from '@/server/functions/api-keys';
import { listReposServerFn } from '@/server/functions/repo';

function getPermissionDisplay(scopes: string[] | null): string {
  if (!scopes || scopes.length === 0) return 'Default';
  if (scopes.includes('admin')) return 'Full access';
  if (scopes.includes('write')) return 'Read & write';
  if (scopes.includes('read')) return 'Read only';
  return 'Default';
}

const snippetsQueryOptions = () =>
  queryOptions({
    queryKey: ['token-snippets'],
    queryFn: async () => {
      const [envSnippetHtml, sdkSnippetHtml] = await Promise.all([
        highlightCode(ENV_SNIPPET, 'bash'),
        highlightCode(SDK_INIT_SNIPPET, 'typescript'),
      ]);
      return { envSnippetHtml, sdkSnippetHtml };
    },
  });

function TokensSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Icon name="key" />
          API Tokens
        </h1>
        <Skeleton className="h-9 w-44 rounded" />
      </div>
      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Token Name</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-32 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-6 rounded" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const tokenSearchSchema = z.object({
  search: z.string().optional(),
  tags: z.array(z.string()).catch([]).optional(),
});

const EMPTY_TAGS: string[] = [];

export const Route = createFileRoute('/$orgSlug/tokens')({
  ssr: false,
  validateSearch: tokenSearchSchema,
  loaderDeps: ({ search: { search, tags } }) => ({ search, tags }),
  loader: async ({ context, deps }) => {
    await Promise.all([
      ensureOrPrefetchInfiniteQuery(
        context.queryClient,
        ['tokens-page', context.org.slug],
        tokensInfiniteQueryOptions(context.org.slug, { search: deps.search, tags: deps.tags })
      ),
      context.queryClient.ensureQueryData(snippetsQueryOptions()),
    ]);
  },
  pendingComponent: TokensSkeleton,
  errorComponent: ErrorState,
  component: TokensPage,
});

type Token = {
  id: string;
  name: string | null;
  suffix: string | null;
  scopes: Scope[] | null;
  repoIds: string[] | null;
  tags: Record<string, string>;
  createdAt: Date;
  lastRequest: Date | null;
};

const SCOPE_GROUPS: { name: string; scopes: { id: Scope; label: string; description: string }[] }[] = [
  {
    name: 'Permissions',
    scopes: [
      { id: 'read', label: 'read', description: 'Clone, fetch, and view repositories and files' },
      { id: 'write', label: 'write', description: 'Push code, create and manage repositories' },
      { id: 'admin', label: 'admin', description: 'Full access including API key management' },
    ],
  },
];

const EXPIRATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'never', label: 'None' },
  { value: '604800', label: '7 days' },
  { value: '2592000', label: '30 days' },
  { value: '7776000', label: '90 days' },
  { value: '31536000', label: '1 year' },
];

function TokenScopeList({ orgSlug, repoIds }: { orgSlug: string; repoIds: string[] }) {
  const sortedKey = useMemo(() => [...repoIds].sort().join(','), [repoIds]);
  const { data, isLoading } = useQuery({
    queryKey: ['token-scope-repos', orgSlug, sortedKey],
    queryFn: () => listReposServerFn({ data: { orgSlug, repoIds, limit: Math.max(repoIds.length, 1) } }),
    staleTime: 60_000,
  });

  if (isLoading) {
    return <p className="text-sm text-mesa-muted">Loading...</p>;
  }

  const repos = data?.repos ?? [];
  const foundIds = new Set(repos.map((r) => r.id));
  const missing = repoIds.filter((id) => !foundIds.has(id));

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-mesa-muted">
        Scoped to {repoIds.length} {repoIds.length === 1 ? 'repository' : 'repositories'}
      </div>
      <ul className="space-y-1 max-h-[240px] overflow-y-auto">
        {repos.map((r) => (
          <li key={r.id} className="text-sm truncate">
            {r.name}
          </li>
        ))}
        {missing.map((id) => (
          <li key={id} className="text-sm text-mesa-muted italic truncate">
            Deleted repo ({id.slice(0, 8)})
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  return `${diffInDays} days ago`;
}

function TokensPage() {
  const { org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const { search: searchFromUrl, tags: tagsFromSearch } = Route.useSearch();
  const tags = tagsFromSearch ?? EMPTY_TAGS;
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const gitBaseUrl = useGitBaseUrl();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;

  const queryOpts = { search: searchFromUrl, tags };
  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    ...tokensInfiniteQueryOptions(org.slug, queryOpts),
    placeholderData: keepPreviousData,
  });

  const tokens = useMemo<Token[]>(
    () =>
      (data?.pages ?? []).flatMap((page) =>
        page.apiKeys.map((key) => {
          const perms = key.permissions ? JSON.parse(key.permissions) : null;
          return {
            id: key.id,
            name: key.name,
            suffix: key.suffix ?? null,
            scopes: (perms?.mesa ?? []) as Scope[],
            repoIds: key.repoIds ?? null,
            tags: key.tags ?? {},
            createdAt: key.createdAt,
            lastRequest: key.lastRequest,
          };
        })
      ),
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;
  const tagCatalog = data?.pages[0]?.tagCatalog ?? {};
  const { data: snippets } = useSuspenseQuery(snippetsQueryOptions());

  const envSnippetHtml = snippets.envSnippetHtml;
  const sdkSnippetHtml = snippets.sdkSnippetHtml;

  const searchValue = searchFromUrl ?? '';

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({ replace: true, search: (prev) => ({ ...prev, search: value || undefined }) });
    },
    [navigate]
  );

  const handleResetFilters = useCallback(() => {
    navigate({ search: (prev) => ({ ...prev, search: undefined, tags: undefined }) });
  }, [navigate]);

  const invalidateTokens = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['tokens-page', org.slug] });
  }, [queryClient, org.slug]);

  const [activeTab, setActiveTab] = useState<'git' | 'sdk'>('sdk');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<Token | null>(null);
  const [newTokenName, setNewTokenName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Scope[]>([...DEFAULT_SCOPES]);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTokenTags, setNewTokenTags] = useState<Record<string, string>>({});
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [selectedRepos, setSelectedRepos] = useState<RepoOption[]>([]);
  const tagKeyRef = useRef<HTMLInputElement>(null);

  const addTag = () => {
    if (!tagKey.trim() || !tagValue.trim()) return;
    setNewTokenTags({ ...newTokenTags, [tagKey.trim().toLowerCase()]: tagValue.trim() });
    setTagKey('');
    setTagValue('');
    tagKeyRef.current?.focus();
  };

  const hasScopes = selectedScopes.length > 0;
  const isAdmin = selectedScopes.includes('admin');

  const toggleScope = (scopeId: Scope) => {
    if (scopeId === 'admin') {
      setSelectedScopes((prev) => (prev.includes('admin') ? [...DEFAULT_SCOPES] : ['admin']));
    } else if (!isAdmin) {
      setSelectedScopes((prev) => (prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]));
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenName.trim() || newTokenName.length < 3 || !hasScopes) return;

    setIsCreating(true);
    setError(null);

    try {
      const scopes = selectedScopes.length > 0 ? selectedScopes : [...DEFAULT_SCOPES];
      const tagsToSend = Object.keys(newTokenTags).length > 0 ? newTokenTags : undefined;
      const repoIds = selectedRepos.length > 0 ? selectedRepos.map((r) => r.id) : null;
      const result = await createApiKeyServerFn({
        data: {
          orgSlug: org.slug,
          name: newTokenName.trim(),
          scopes,
          repoIds,
          expiresInSeconds,
          tags: tagsToSend,
        },
      });
      setCreatedToken(result.key);
      await invalidateTokens();
    } catch (err) {
      pushError(err);
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!tokenToRevoke) return;

    setIsRevoking(true);
    setError(null);

    try {
      await revokeApiKeyServerFn({ data: { orgSlug: org.slug, keyId: tokenToRevoke.id } });
      setTokenToRevoke(null);
      setIsRevokeDialogOpen(false);
      await invalidateTokens();
    } catch (err) {
      pushError(err);
      setError(err instanceof Error ? err.message : 'Failed to revoke token');
    } finally {
      setIsRevoking(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewTokenName('');
    setSelectedScopes([...DEFAULT_SCOPES]);
    setExpiresInSeconds(null);
    setIsAdvancedOpen(false);
    setCreatedToken(null);
    setCopied(false);
    setError(null);
    setNewTokenTags({});
    setTagKey('');
    setTagValue('');
    setSelectedRepos([]);
  };

  const isFiltered = searchValue !== '' || tags.length > 0;
  const isEmpty = tokens.length === 0 && !isFiltered && !isFetching;

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const filterKey = `${searchFromUrl ?? ''}-${tags.join(',')}`;
  const prevFilterKey = useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    setRowSelection({});
  }
  const selectedTokenIds = useMemo(() => Object.keys(rowSelection).filter((id) => rowSelection[id]), [rowSelection]);
  const selectedCount = selectedTokenIds.length;
  const [isBulkRevokeDialogOpen, setIsBulkRevokeDialogOpen] = useState(false);
  const [isBulkRevoking, setIsBulkRevoking] = useState(false);

  const handleBulkRevoke = async () => {
    setIsBulkRevoking(true);
    try {
      await bulkRevokeApiKeysServerFn({ data: { orgSlug: org.slug, keyIds: selectedTokenIds } });
      setRowSelection({});
      setIsBulkRevokeDialogOpen(false);
      await invalidateTokens();
    } catch (err) {
      pushError(err);
    } finally {
      setIsBulkRevoking(false);
    }
  };

  const columns = useMemo<ColumnDef<Token>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) => {
          const allSelected = table.getIsAllPageRowsSelected();
          const someSelected = table.getIsSomePageRowsSelected();
          return (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
              aria-label="Select all"
            />
          );
        },
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(!!checked)}
            aria-label="Select row"
          />
        ),
      },
      {
        id: 'name',
        header: 'Token Name',
        cell: ({ row }) => <span className="font-medium">{row.original.name ?? 'Unnamed token'}</span>,
      },
      {
        id: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <span className="text-mesa-muted font-mono text-sm">
            {row.original.suffix ? `••••${row.original.suffix}` : '—'}
          </span>
        ),
      },
      {
        id: 'permissions',
        header: 'Permissions',
        cell: ({ row }) => (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-mesa-muted cursor-default">{getPermissionDisplay(row.original.scopes)}</span>
              </TooltipTrigger>
              <TooltipContent>
                {row.original.scopes && row.original.scopes.length > 0 ? (
                  <ul className="text-xs space-y-0.5">
                    {row.original.scopes.map((scope) => (
                      <li key={scope}>{scope}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-xs">Default permissions</span>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: ({ row }) => {
          const repoIds = row.original.repoIds;
          if (!repoIds || repoIds.length === 0) {
            return <span className="text-mesa-muted text-sm">Org-wide</span>;
          }
          return (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" aria-label="View scoped repositories">
                  <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-mesa-border">
                    {repoIds.length} {repoIds.length === 1 ? 'repo' : 'repos'}
                  </Badge>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[280px] p-3">
                <TokenScopeList orgSlug={org.slug} repoIds={repoIds} />
              </PopoverContent>
            </Popover>
          );
        },
      },
      {
        id: 'tags',
        header: 'Tags',
        cell: ({ row }) => {
          const token = row.original;
          return (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="flex flex-wrap gap-1 min-h-[24px] cursor-pointer">
                  {Object.entries(token.tags).length > 0 ? (
                    <>
                      {Object.entries(token.tags)
                        .slice(0, 3)
                        .map(([k, v]) => (
                          <Badge key={k} variant="secondary" className="text-xs">
                            {k}: {v}
                          </Badge>
                        ))}
                      {Object.keys(token.tags).length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{Object.keys(token.tags).length - 3} more
                        </Badge>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-mesa-muted">Add tags</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[300px] p-3">
                <LabelEditor
                  labels={Object.entries(token.tags).map(([key, value]) => ({ key, value }))}
                  facets={{}}
                  onAdd={async (key, value) => {
                    await updateApiKeyTagsServerFn({
                      data: { orgSlug: org.slug, keyId: token.id, tags: { [key]: value } },
                    });
                    await invalidateTokens();
                  }}
                  onRemove={async (key) => {
                    await updateApiKeyTagsServerFn({
                      data: { orgSlug: org.slug, keyId: token.id, tags: { [key]: null } },
                    });
                    await invalidateTokens();
                  }}
                />
              </PopoverContent>
            </Popover>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        cell: ({ row }) => <span className="text-mesa-muted">{formatRelativeTime(row.original.createdAt)}</span>,
      },
      {
        id: 'lastUsed',
        header: 'Last Used',
        cell: ({ row }) => (
          <span className="text-mesa-muted">
            {row.original.lastRequest ? formatRelativeTime(row.original.lastRequest) : 'Never'}
          </span>
        ),
      },
      {
        id: 'actions',
        size: 50,
        enableHiding: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Icon name="ellipsis" className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-mesa-red focus:text-mesa-red"
                onClick={() => {
                  setTokenToRevoke(row.original);
                  setIsRevokeDialogOpen(true);
                }}
              >
                Revoke token
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [org.slug, invalidateTokens]
  );

  const table = useReactTable({
    data: tokens,
    columns,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Icon name="key" />
          API Tokens
        </h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Icon name="plus" className="h-4 w-4" />
          Create API Token
        </Button>
      </div>

      {isEmpty ? (
        <div className="border border-mesa-border">
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="key" className="h-12 w-12 text-mesa-muted mb-4" />
            <h3 className="text-lg font-semibold mb-2">No API tokens yet</h3>
            <p className="text-mesa-muted text-center mb-4">Create an API token to start using Mesa services.</p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Icon name="plus" className="h-4 w-4" />
              Create API Token
            </Button>
          </div>
        </div>
      ) : (
        <DataTable
          table={table}
          isFetching={isFetching}
          noResultsMessage="No tokens match your filters."
          toolbar={
            selectedCount > 0 ? (
              <DataTableSelectionToolbar
                selectedCount={selectedCount}
                onClearSelection={() => setRowSelection({})}
                itemLabel="token"
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-mesa-red hover:text-mesa-red"
                  onClick={() => setIsBulkRevokeDialogOpen(true)}
                >
                  <Icon name="ban" className="mr-2 size-3.5" />
                  Revoke
                </Button>
              </DataTableSelectionToolbar>
            ) : (
              <DataTableToolbar
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Filter tokens..."
                isFiltered={isFiltered}
                onResetFilters={handleResetFilters}
                filters={
                  tagCatalog && Object.keys(tagCatalog).length > 0 ? (
                    <LabelFilter
                      facets={tagCatalog}
                      selectedFilters={tags}
                      onFilterChange={(filters) => {
                        navigate({ search: (prev) => ({ ...prev, tags: filters.length > 0 ? filters : undefined }) });
                      }}
                    />
                  ) : null
                }
              >
                <DataTableViewOptions table={table} />
              </DataTableToolbar>
            )
          }
          pagination={
            totalCount > 0 ? (
              <div className="flex flex-col items-center gap-2 py-4">
                <span className="text-sm text-mesa-muted">
                  Showing {tokens.length} of {totalCount} tokens
                </span>
                {hasNextPage && (
                  <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetching}>
                    {isFetching ? 'Loading...' : 'Load more'}
                  </Button>
                )}
              </div>
            ) : null
          }
        />
      )}

      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-1 border-b border-mesa-border">
          <button
            type="button"
            onClick={() => setActiveTab('sdk')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'sdk'
                ? 'border-mesa-green text-mesa-text'
                : 'border-transparent text-mesa-muted hover:text-mesa-text'
            }`}
          >
            SDK
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('git')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'git'
                ? 'border-mesa-green text-mesa-text'
                : 'border-transparent text-mesa-muted hover:text-mesa-text'
            }`}
          >
            Git / CLI
          </button>
        </div>

        {activeTab === 'git' ? (
          <div className="space-y-4">
            <p className="text-sm text-mesa-muted">
              Use your token to authenticate git operations. Include it in your clone URL:
            </p>
            <CloneUrlSnippet gitBaseUrl={gitBaseUrl} orgSlug={org.slug} />
            <p className="text-sm text-mesa-muted">
              Replace <code className="bg-mesa-cream-dark px-1 py-0.5 text-xs rounded">YOUR_TOKEN</code> with your token
              and <code className="bg-mesa-cream-dark px-1 py-0.5 text-xs rounded">REPO</code> with your repository
              name.
            </p>
            <div className="pt-2">
              <Button variant="outline" asChild>
                <Link to="/$orgSlug/repositories" params={{ orgSlug: org.slug }}>
                  <Icon name="book-bookmark" className="mr-2 h-4 w-4" />
                  Go to Repositories
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-medium">1. Add to environment</h3>
              <p className="text-sm text-mesa-muted">Set your API key as an environment variable:</p>
              <CodeBlock code={ENV_SNIPPET} highlightedHtml={envSnippetHtml} language="bash" filename=".env" />
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">2. Set up client SDK</h3>
              <p className="text-sm text-mesa-muted">Initialize the client:</p>
              <CodeBlock
                code={SDK_INIT_SNIPPET}
                highlightedHtml={sdkSnippetHtml}
                language="typescript"
                filename="index.ts"
                docsUrl="https://docs.mesa.dev/content/getting-started/quickstart"
              />
            </div>
          </div>
        )}
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdToken ? 'Token Created' : 'Create API Token'}</DialogTitle>
            <DialogDescription>
              {createdToken
                ? "Copy your token now. You won't be able to see it again."
                : 'Give your token a name to help you identify it later.'}
            </DialogDescription>
          </DialogHeader>

          {createdToken ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-mesa-cream-dark p-3 text-sm break-all">{createdToken}</code>
                <Button variant="outline" size="icon" onClick={() => handleCopy(createdToken)}>
                  {copied ? (
                    <Icon name="check" solid className="h-4 w-4 text-green-600" />
                  ) : (
                    <Icon name="copy" className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <span>⚠️</span>
                <span>Save this token — you will not see it again</span>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="token-name" className="text-sm font-medium">
                  Token name
                </label>
                <Input
                  id="token-name"
                  placeholder="e.g., production, development"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTokenName.length >= 3 && hasScopes) handleCreateToken();
                  }}
                />
                <p className="text-xs text-mesa-muted">Minimum 3 characters</p>
              </div>

              <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
                <div className="flex items-center justify-between gap-4">
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-mesa-text/80 transition-colors">
                    {isAdvancedOpen ? (
                      <Icon name="chevron-down" className="h-4 w-4" />
                    ) : (
                      <Icon name="chevron-right" className="h-4 w-4" />
                    )}
                    Permissions
                  </CollapsibleTrigger>

                  <div className="flex items-center gap-2 shrink-0">
                    <label htmlFor="token-expiration" className="text-sm font-medium">
                      Expiration
                    </label>
                    <Select
                      value={expiresInSeconds === null ? 'never' : String(expiresInSeconds)}
                      onValueChange={(v) => setExpiresInSeconds(v === 'never' ? null : Number(v))}
                    >
                      <SelectTrigger id="token-expiration" className="w-24">
                        {
                          EXPIRATION_OPTIONS.find(
                            (o) => o.value === (expiresInSeconds === null ? 'never' : String(expiresInSeconds))
                          )!.label
                        }
                      </SelectTrigger>
                      <SelectContent>
                        {EXPIRATION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <CollapsibleContent className="pt-4 space-y-4">
                  {SCOPE_GROUPS.map((group) => (
                    <div key={group.name} className="space-y-2">
                      <h4 className="text-xs font-semibold text-mesa-muted uppercase tracking-wide">{group.name}</h4>
                      <div className="space-y-2">
                        {group.scopes.map((scope) => (
                          <label key={scope.id} className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                              id={scope.id}
                              checked={scope.id === 'admin' ? isAdmin : isAdmin || selectedScopes.includes(scope.id)}
                              onCheckedChange={() => toggleScope(scope.id)}
                              disabled={scope.id !== 'admin' && isAdmin}
                              className="mt-0.5"
                            />
                            <div className="space-y-0.5">
                              <span className="text-sm font-medium">{scope.label}</span>
                              <p className="text-xs text-mesa-muted">{scope.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              <div className="space-y-2">
                <label className="text-sm font-medium">Repository access</label>
                <RepoMultiSelect orgSlug={org.slug} selected={selectedRepos} onChange={setSelectedRepos} />
                <p className="text-xs text-mesa-muted">
                  Leave empty for org-wide access. Selecting repositories scopes this token to only those repos.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tags</label>
                <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                  {Object.entries(newTokenTags).map(([k, v]) => (
                    <Badge key={k} variant="secondary" className="gap-1">
                      {k}: {v}
                      <button
                        type="button"
                        onClick={() => {
                          const next = { ...newTokenTags };
                          delete next[k];
                          setNewTokenTags(next);
                        }}
                        className="ml-0.5 hover:text-mesa-red"
                      >
                        <Icon name="x" className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    ref={tagKeyRef}
                    aria-label="Tag key"
                    placeholder="key"
                    value={tagKey}
                    onChange={(e) => setTagKey(e.target.value)}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (tagKey.trim() && tagValue.trim()) {
                          addTag();
                        }
                      }
                    }}
                  />
                  <Input
                    aria-label="Tag value"
                    placeholder="value"
                    value={tagValue}
                    onChange={(e) => setTagValue(e.target.value)}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if ((e.key === 'Tab' || e.key === 'Enter') && tagKey.trim() && tagValue.trim()) {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!tagKey.trim() || !tagValue.trim()}
                    onClick={addTag}
                  >
                    Add
                  </Button>
                </div>
              </div>

              {!hasScopes && <p className="text-sm text-mesa-red">Token needs at least 1 permission</p>}
              {error && <p className="text-sm text-mesa-red">{error}</p>}
            </div>
          )}

          <DialogFooter>
            {createdToken ? (
              <Button onClick={handleCloseCreateDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog} disabled={isCreating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateToken} disabled={newTokenName.length < 3 || !hasScopes || isCreating}>
                  {isCreating ? 'Creating...' : 'Create Token'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isRevokeDialogOpen} onOpenChange={setIsRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Token</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this token? Any applications using it will no longer be able to access
              Mesa.
            </AlertDialogDescription>
            {tokenToRevoke && (
              <div className="border border-mesa-border px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-mesa-text">{tokenToRevoke.name ?? 'Unnamed token'}</span>
                  {tokenToRevoke.suffix && (
                    <span className="text-mesa-muted font-mono text-xs">{`····${tokenToRevoke.suffix}`}</span>
                  )}
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTokenToRevoke(null)} disabled={isRevoking}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeToken}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={isRevoking}
            >
              {isRevoking ? 'Revoking...' : 'Revoke Token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isBulkRevokeDialogOpen} onOpenChange={setIsBulkRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revoke {selectedCount} {selectedCount === 1 ? 'token' : 'tokens'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke {selectedCount === 1 ? 'this token' : `these ${selectedCount} tokens`}?
              Any applications using {selectedCount === 1 ? 'it' : 'them'} will no longer be able to access Mesa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkRevoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkRevoke}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={isBulkRevoking}
            >
              {isBulkRevoking ? 'Revoking...' : `Revoke ${selectedCount} ${selectedCount === 1 ? 'token' : 'tokens'}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CloneUrlSnippet({ gitBaseUrl, orgSlug }: { gitBaseUrl: string; orgSlug: string }) {
  const [copied, setCopied] = useState(false);
  const protocol = gitBaseUrl.startsWith('https://') ? 'https' : 'http';
  const urlWithoutProtocol = gitBaseUrl.replace(/^https?:\/\//, '');
  const snippet = `git clone ${protocol}://YOUR_TOKEN@${urlWithoutProtocol}/${orgSlug}/REPO.git`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-mesa-border overflow-hidden">
      <div className="flex items-center justify-between bg-mesa-cream-dark px-4 py-2 border-b border-mesa-border">
        <span className="text-sm text-mesa-muted">bash</span>
        <Button variant="ghost" size="sm" className="h-6 px-2" onClick={handleCopy}>
          {copied ? (
            <Icon name="check" solid className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Icon name="copy" className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      <div className="p-4 text-sm overflow-x-auto bg-mesa-card font-mono">
        <code>{snippet}</code>
      </div>
    </div>
  );
}
